import { cn } from "@/lib/utils";
import { Button } from "@/styles/ui/button";
import {
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/styles/ui/dialog";
import { X } from "lucide-react";
import FilledButton from "./button";

type ModalHeaderProps = {
  title: string;
  description?: string;
  showCloseButton?: boolean;
  className?: string;
};

export const ModalHeader = ({
  title,
  description,
  showCloseButton = true,
  className,
}: ModalHeaderProps) => {
  return (
    <DialogHeader
      className={cn(
        "flex flex-col items-start justify-between px-3 relative gap-1.5",
        className,
      )}
    >
      <span className="headline-1-semibold text-primary w-fit">{title}</span>
      {showCloseButton && (
        <DialogClose asChild>
          <Button
            variant="ghost"
            className="absolute p-0 !mt-0 w-[22px] h-[22px] [&_svg]:size-[14px] bg-white bg-button top-0 right-0"
          >
            <X className="text-neutral-600" />
          </Button>
        </DialogClose>
      )}
      {description && (
        <DialogDescription className="flex flex-wrap gap-1 rounded-sm !mt-0">
          <span className="label-1-normal-medium text-[#737373]">
            {description}
          </span>
        </DialogDescription>
      )}
    </DialogHeader>
  );
};

type ModalFooterProps = {
  confirmLabel: string;
  onConfirm?: () => void;
  cancelLabel?: string;
  onCancel?: () => void;
  isLoading?: boolean;
  className?: string;
  cancelClassName?: string;
  confirmClassName?: string;
  disabled?: boolean;
};

export const ModalFooterFull = ({
  cancelLabel,
  onCancel,
  confirmLabel,
  onConfirm,
  isLoading,
  disabled,
  className,
  cancelClassName,
  confirmClassName,
}: ModalFooterProps) => {
  return (
    <DialogFooter
      className={cn("flex flex-row w-full justify-between", className)}
    >
      {cancelLabel && (
        <DialogClose asChild>
          <FilledButton
            className={cn(
              `w-full h-9 bg-white rounded-md shadow-none !border !border-neutral-200 !border-solid hover:bg-black/[.03] active:bg-black/6 active:border-neutral-200 focus:bg-black/6 focus:shadow-none focus:border-neutral-200 focus:text-primary text-primary label-1-normal-medium`,
              {
                "border-neutral-200 bg-black/6 cursor-not-allowed": isLoading,
              },
              "disabled:border-neutral-200 disabled:opacity-50 disabled:text-[#8B8B8B]",
              cancelClassName,
            )}
            type="reset"
            onClick={onCancel}
            text={cancelLabel}
            disabled={isLoading}
          />
        </DialogClose>
      )}
      <FilledButton
        className={cn(
          `w-full h-9 bg-neutral-900 rounded-md shadow-none text-white hover:bg-neutral-800 active:bg-neutral-700 focus:bg-neutral-700 focus:shadow-none label-1-normal-medium`,
          isLoading && "bg-neutral-700 cursor-not-allowed [&_svg]:!text-white",
          "disabled:bg-[#8B8B8B] disabled:cursor-not-allowed disabled:placeholder-opacity-50",
          confirmClassName,
        )}
        type="submit"
        onClick={onConfirm}
        isLoading={isLoading}
        text={confirmLabel}
        disabled={disabled}
      />
    </DialogFooter>
  );
};
